import { useEffect } from 'react';

import { Box, Button, HStack } from '@chakra-ui/react';
import { useSearchParams } from 'next/navigation';

import { IconRenderer } from '@components/IconComponent';
import { useAppDispatch, useAppSelector } from '@hooks/store';
import { useGetDomainsQuery } from '@modules/common/api';
import { clearFilters, setFindMentorFilters } from '@modules/find-mentor/slice';

const DOMAIN_QUERY_NAME = 'domain_id';

const DomainTabs = () => {
    const dispatch = useAppDispatch();
    const searchParams = useSearchParams();
    const { domainIds } = useAppSelector(state => state.reducer.mentorFilters);

    useEffect(() => {
        const domainId = searchParams.get(DOMAIN_QUERY_NAME);

        if (domainId && !domainIds?.includes(domainId)) {
            dispatch(clearFilters());
            dispatch(setFindMentorFilters({ domainIds: [domainId] }));
        }
    }, [dispatch, searchParams, domainIds]);

    const handleDomainIdToggle = (value: string) => {
        const isSelected = domainIds?.[0] === value;

        if (isSelected) {
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.delete(DOMAIN_QUERY_NAME);
            window.history.pushState({}, '', `/find-mentor?${newParams.toString()}`);
            dispatch(setFindMentorFilters({ domainIds: [] }));
            dispatch(clearFilters());
        } else {
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.set(DOMAIN_QUERY_NAME, value);
            window.history.pushState({}, '', `/find-mentor?${newParams.toString()}`);
            dispatch(clearFilters());
            dispatch(setFindMentorFilters({ domainIds: [value] }));
        }
    };

    const { data: domainsData } = useGetDomainsQuery();

    return (
        <Box
            width="100%"
            overflowX={{ base: 'auto', md: 'visible' }}
            sx={{
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
            }}
        >
            <HStack
                wrap={{ base: 'nowrap', md: 'wrap' }}
                gap="16px"
                width={{ base: 'max-content', md: '100%' }}
                pb={{ base: 2, md: 0 }}
            >
                {domainsData?.domain.map(domain => (
                    <Box key={domain.id}>
                        <Button
                            onClick={() => handleDomainIdToggle(domain.id)}
                            variant="outline"
                            sx={{
                                fontWeight: '400',
                                padding: '12px !important',
                                borderRadius: '6px',
                                whiteSpace: 'nowrap',
                                color:
                                    domainIds && domainIds[0] === domain.id ? 'white' : 'mainText',
                                background:
                                    domainIds && domainIds[0] === domain.id ? 'primary.light' : '',
                                '&:hover': {
                                    background:
                                        domainIds && domainIds[0] === domain.id
                                            ? 'primary.dark'
                                            : 'primary.light',
                                    color:
                                        domainIds && domainIds[0] === domain.id ? 'white' : 'white',
                                },
                            }}
                            leftIcon={
                                <IconRenderer iconName={domain.icon} iconPack={domain.iconPack} />
                            }
                        >
                            {domain.name}
                        </Button>
                    </Box>
                ))}
            </HStack>
        </Box>
    );
};

export default DomainTabs;

export { DOMAIN_QUERY_NAME };
